import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import HisobAppPreview from '@/components/marketing/HisobAppPreview';
import { useActiveMarketingItems, useMarketingCampaigns } from '@/hooks/use-marketing';
import {
  bpsToPercent, campaignStatusLabel, campaignStatusVariant, formatPeriod, promotionLabel,
  type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

const SLUG_TO_TYPE: Record<string, PromotionType> = {
  vip: 'VIP_STORES',
  categories: 'PROMOTED_CATEGORIES',
  popular: 'POPULAR_STORES',
  'big-cashback': 'BIG_CASHBACK',
};

export default function AdminMarketingPlacement() {
  const { t } = useTranslation();
  const { slug = 'vip' } = useParams();
  const type = SLUG_TO_TYPE[slug] ?? 'VIP_STORES';

  const { data: campaigns = [] } = useMarketingCampaigns({ promotion_type: type });
  const { data: items = [] } = useActiveMarketingItems(type);

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{promotionLabel[type]}</h1>
          <p className="text-sm text-muted-foreground">{t('mkt_placement.subtitle')}</p>
        </div>
        <Button asChild><Link to="/admin/marketing/campaigns/new"><Plus size={16} className="mr-1" /> {t('mkt_placement.create')}</Link></Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-4">
              <h2 className="text-sm font-medium text-foreground">{t('mkt_placement.liveItems')} ({(items as any[]).length})</h2>
            </div>
            {(items as any[]).length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_placement.noLiveItems')}</p>
            ) : (
              <div className="divide-y divide-border">
                {(items as any[]).map((it) => (
                  <div key={it.item_id} className="flex flex-wrap items-center justify-between gap-2 p-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {it.display_order}. {it.promo_title || it.merchant_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {it.merchant_name}
                        {it.category_name ? ` · ${it.category_name}` : ''}
                        {it.cashback_bps ? ` · ${bpsToPercent(it.cashback_bps)} ${t('mkt_placement.cashbackWord')}` : ''}
                      </p>
                    </div>
                    <Link to={`/admin/marketing/campaigns/${it.campaign_id}`} className="text-xs text-primary hover:underline">
                      {it.campaign_name}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-4">
              <h2 className="text-sm font-medium text-foreground">{t('mkt_placement.allCampaigns')} ({campaigns.length})</h2>
            </div>
            {campaigns.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-muted-foreground">{t('mkt_placement.empty')}</p>
                <Button asChild className="mt-3" size="sm"><Link to="/admin/marketing/campaigns/new">{t('mkt_placement.createPlus')}</Link></Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {campaigns.map((c: any) => (
                  <Link key={c.id} to={`/admin/marketing/campaigns/${c.id}`} className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/30">
                    <div>
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{formatPeriod(c.start_at, c.end_at)}</p>
                    </div>
                    <Badge variant={campaignStatusVariant[c.status as CampaignStatus]}>
                      {campaignStatusLabel[c.status as CampaignStatus]}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <HisobAppPreview
          promotionType={type}
          items={(items as any[]).map((i) => ({
            id: i.item_id,
            merchant_name: i.merchant_name,
            category_name: i.category_name,
            promo_title: i.promo_title,
            promo_subtitle: i.promo_subtitle,
            promo_image_url: i.promo_image_url,
            cashback_bps: i.cashback_bps,
          }))}
        />
      </div>
    </div>
  );
}
